import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Layers, X, Cog, AlertTriangle, HelpCircle } from 'lucide-react';
import { Reorder, AnimatePresence } from 'framer-motion';
import MapLayerItem from './MapLayerItem';
import { MAP_LAYER_TOUR_STORAGE_KEY } from './mapLayerTourSteps';
import ColorPicker from './ColorPicker';
import {
  MAP_MAX_FEATURES_PER_LAYER_DEFAULT,
  MAP_MAX_FEATURES_PER_LAYER_MIN,
  MAP_MAX_FEATURES_PER_LAYER_MAX,
  MAP_MAX_FEATURES_PER_LAYER_SLOW_WARN,
  parseMapMaxFeaturesPerLayer
} from '../../../utils/mapUtils';

const MAP_FEATURE_PRESETS = [10000, 25000, 50000, 100000];

const MapLayerList = ({
  layers,
  readOnly = false,
  workspaceId,
  chatId,
  availableDatasets = [],
  maxFeaturesPerLayer = MAP_MAX_FEATURES_PER_LAYER_DEFAULT,
  onMaxFeaturesPerLayerChange,
  onToggleVisibility,
  onRemoveLayer,
  onUpdateTheme,
  onUpdateLayerClassification,
  onUpdateLayerMetric,
  onUpdateLayerColorProperty,
  onPersistLayerStyle,
  onAddLayer,
  onPreviewLayer,
  onOpenBuilder,
  onFilterLayer,
  onFlyToLayer,
  onReorderLayers
}) => {

  const [activePickerId, setActivePickerId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(layers.length === 0);
  const [tourLayerId, setTourLayerId] = useState(null);
  const [tourPersistOnFinish, setTourPersistOnFinish] = useState(false);
  const [pendingTourLayerId, setPendingTourLayerId] = useState(null);
  const prevLayerCount = useRef(layers.length);
  const knownLayerIdsRef = useRef(new Set());
  const layerListInitializedRef = useRef(false);
  const [isMapDisplayMenuOpen, setIsMapDisplayMenuOpen] = useState(false);
  const mapDisplayMenuRef = useRef(null);
  const [draftMaxFeatures, setDraftMaxFeatures] = useState(String(maxFeaturesPerLayer));

  useEffect(() => {
    setDraftMaxFeatures(String(maxFeaturesPerLayer));
  }, [maxFeaturesPerLayer]);

  useEffect(() => {
    if (!isMapDisplayMenuOpen) return;
    const handleClickOutside = (event) => {
      if (mapDisplayMenuRef.current && !mapDisplayMenuRef.current.contains(event.target)) {
        setIsMapDisplayMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMapDisplayMenuOpen]);

  const commitDraftMaxFeatures = () => {
    if (!onMaxFeaturesPerLayerChange) return;
    const next = parseMapMaxFeaturesPerLayer(draftMaxFeatures);
    onMaxFeaturesPerLayerChange(next);
    setDraftMaxFeatures(String(next));
  };

  // Filter out the "preview-layer" which is only for map display, not the list
  const displayLayers = layers.filter(l => l.id !== 'preview-layer');
  const activeLayer = layers.find(l => l.id === activePickerId);

  const layersExceedingDisplayCap = useMemo(
    () =>
      layers.filter((l) => {
        if (l.status === 'loading' || l.status === 'error') return false;
        const n = l.data?.features?.length ?? 0;
        return n > maxFeaturesPerLayer;
      }),
    [layers, maxFeaturesPerLayer]
  );

  // Seed known layer ids on first render so existing layers never trigger the tour
  useEffect(() => {
    if (layerListInitializedRef.current) return;
    displayLayers.forEach((layer) => knownLayerIdsRef.current.add(layer.id));
    layerListInitializedRef.current = true;
  }, [displayLayers]);

  // Queue the first newly created layer for onboarding
  useEffect(() => {
    if (readOnly) return;
    if (localStorage.getItem(MAP_LAYER_TOUR_STORAGE_KEY)) return;

    displayLayers.forEach((layer) => {
      if (knownLayerIdsRef.current.has(layer.id)) return;
      knownLayerIdsRef.current.add(layer.id);
      setPendingTourLayerId(layer.id);
    });
  }, [displayLayers, readOnly]);

  // Start onboarding once the new layer has finished loading
  useEffect(() => {
    if (readOnly) return;
    if (localStorage.getItem(MAP_LAYER_TOUR_STORAGE_KEY)) return;
    if (!pendingTourLayerId || tourLayerId) return;

    const layer = displayLayers.find((l) => l.id === pendingTourLayerId);
    if (!layer || (layer.status !== 'loaded' && layer.status !== 'error')) return;

    const timer = setTimeout(() => {
      setIsMinimized(false);
      setTourPersistOnFinish(true);
      setTourLayerId(pendingTourLayerId);
      setPendingTourLayerId(null);
    }, 600);

    return () => clearTimeout(timer);
  }, [displayLayers, pendingTourLayerId, tourLayerId, readOnly]);

  const handleLayerTourFinish = () => {
    if (tourPersistOnFinish) {
      localStorage.setItem(MAP_LAYER_TOUR_STORAGE_KEY, 'true');
    }
    setTourLayerId(null);
    setTourPersistOnFinish(false);
  };

  const tourableLayer = displayLayers.find(
    (layer) => layer.status === 'loaded' || layer.status === 'error'
  );

  const handleManualTour = () => {
    if (!tourableLayer || tourLayerId) return;
    setIsMinimized(false);
    setTourPersistOnFinish(false);
    setTourLayerId(tourableLayer.id);
  };

  const renderTourHelpButton = (minimized = false) => {
    if (readOnly || !tourableLayer) return null;

    return (
      <button
        type="button"
        onClick={handleManualTour}
        disabled={Boolean(tourLayerId)}
        className={minimized
          ? 'bg-white dark:bg-gray-900 p-2.5 rounded-[16px] shadow-2xl border border-gray-100 dark:border-gray-800 transition-all hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center disabled:opacity-40 disabled:pointer-events-none text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400'
          : 'p-1.5 rounded-lg transition-colors text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-40 disabled:pointer-events-none'}
        title="Layer controls guide"
      >
        <HelpCircle size={minimized ? 14 : 16} />
      </button>
    );
  };

  // Auto-minimize when layers are removed (going from some to none)
  // Auto-expand when layers are added
  // But allow manual expansion even when empty
  useEffect(() => {
    // Auto-expand when a new layer is added
    if (prevLayerCount.current < displayLayers.length && displayLayers.length > 0) {
      setIsMinimized(false);
    }
    // Auto-minimize if we HAD layers and now have none
    else if (prevLayerCount.current > 0 && displayLayers.length === 0) {
      setIsMinimized(true);
    }

    prevLayerCount.current = displayLayers.length;
  }, [displayLayers.length]);

  const closePicker = (persist = true) => {
    if (persist && activePickerId) {
      onPersistLayerStyle?.(activePickerId);
    }
    setActivePickerId(null);
  };

  const handleTogglePicker = (id) => {
    if (activePickerId === id) {
      closePicker(true);
      return;
    }
    if (activePickerId) {
      onPersistLayerStyle?.(activePickerId);
    }
    setActivePickerId(id);
  };

  const handleRemove = (id) => {
    if (activePickerId === id) closePicker(false);
    onRemoveLayer(id);
  };

  const handleReorder = (newDisplayOrder) => {
    if (!onReorderLayers) return;

    // We need to merge the new display order back with the hidden layers (like preview-layer)
    const previewLayer = layers.find(l => l.id === 'preview-layer');
    const newFullOrder = [...newDisplayOrder];
    if (previewLayer) {
      newFullOrder.push(previewLayer); // Preview layer always at the end/bottom/not reorderable
    }

    onReorderLayers(newFullOrder);
  };

  const activeLayersCount = displayLayers.filter(l => l.visible).length;
  const totalLayersCount = displayLayers.length;

  return (
    <div
      className="absolute top-6 left-6 z-[60]"
      onMouseMove={(e) => e.stopPropagation()}
      onMouseEnter={(e) => e.stopPropagation()}
    >
      {totalLayersCount === 0 ? null : isMinimized ? (
        <div className="flex flex-col gap-2">
          {renderTourHelpButton(true)}
          <button
            onClick={() => setIsMinimized(false)}
            className="relative bg-white dark:bg-gray-900 p-3 rounded-[16px] shadow-2xl border border-gray-100 dark:border-gray-800 transition-all duration-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-center cursor-pointer"
            title="Show Map Layers"
          >
            <Layers size={16} className="text-gray-600 dark:text-gray-400" />
            {totalLayersCount > 0 && (
              <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-emerald-500 text-white text-[9px] font-black rounded-full flex items-center justify-center px-1">
                {activeLayersCount}
              </div>
            )}
          </button>
        </div>
      ) : (
        // Expanded: Full panel. Picker is a sibling so overflow-hidden does not clip it.
        <>
        <div className="relative flex max-h-[calc(100dvh-3rem)] w-80 flex-col overflow-hidden rounded-[24px] border border-gray-100 bg-white p-3 shadow-2xl transition-all duration-300 dark:border-gray-800 dark:bg-gray-900">
          <div className="flex shrink-0 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 text-gray-600 dark:text-gray-400 rounded-lg">
                <Layers size={14} />
              </div>
              <div>
                <h4 className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-widest">
                  Map Layers
                </h4>
                <span className="text-[7px] font-bold text-gray-400 uppercase tracking-widest">
                  {activeLayersCount} / {totalLayersCount} Active
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1" ref={mapDisplayMenuRef}>
              {renderTourHelpButton()}
              {onMaxFeaturesPerLayerChange && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsMapDisplayMenuOpen((o) => !o)}
                    className={`p-1.5 rounded-lg transition-colors ${isMapDisplayMenuOpen
                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30'
                      : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    title="Map display settings"
                  >
                    <Cog size={16} />
                  </button>
                  {isMapDisplayMenuOpen && (
                    <div
                      className="absolute right-0 top-full mt-1.5 w-[220px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl z-[80] p-2.5 space-y-2"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <p className="text-[9px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                        Features per layer
                      </p>
                      <p className="text-[9px] text-gray-500 dark:text-gray-400 leading-snug">
                        Saved in this browser. Caps what the map draws per layer.
                      </p>
                      <div className="flex gap-1.5 rounded-lg border border-amber-200/90 dark:border-amber-800/50 bg-amber-50/90 dark:bg-amber-950/35 px-2 py-1.5 text-[9px] text-amber-950 dark:text-amber-100/95 leading-snug">
                        <AlertTriangle className="w-3 h-3 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" aria-hidden />
                        <span>
                          Over {MAP_MAX_FEATURES_PER_LAYER_SLOW_WARN.toLocaleString()} can slow the browser.
                          {maxFeaturesPerLayer > MAP_MAX_FEATURES_PER_LAYER_SLOW_WARN && (
                            <> Yours: {maxFeaturesPerLayer.toLocaleString()}.</>
                          )}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {MAP_FEATURE_PRESETS.map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => {
                              onMaxFeaturesPerLayerChange(parseMapMaxFeaturesPerLayer(preset));
                              setIsMapDisplayMenuOpen(false);
                            }}
                            className={`px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-tight border transition-colors ${maxFeaturesPerLayer === preset
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-600'
                              : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                              }`}
                          >
                            {(preset / 1000) >= 1000 ? `${preset / 1_000_000}M` : `${preset / 1000}k`}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={MAP_MAX_FEATURES_PER_LAYER_MIN}
                          max={MAP_MAX_FEATURES_PER_LAYER_MAX}
                          value={draftMaxFeatures}
                          onChange={(e) => setDraftMaxFeatures(e.target.value)}
                          onBlur={commitDraftMaxFeatures}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commitDraftMaxFeatures();
                              setIsMapDisplayMenuOpen(false);
                            }
                          }}
                          className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2 py-1 text-[11px] font-mono text-gray-900 dark:text-white"
                        />
                      </div>
                      <p className="text-[8px] text-gray-400 font-bold uppercase tracking-tighter">
                        {MAP_MAX_FEATURES_PER_LAYER_MIN.toLocaleString()}–{MAP_MAX_FEATURES_PER_LAYER_MAX.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* Add Layer button hidden */}
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Minimize"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />
          {layersExceedingDisplayCap.length > 0 && (
            <div className="mb-2 flex shrink-0 gap-2 rounded-xl border border-amber-200/90 dark:border-amber-800/50 bg-amber-50/95 dark:bg-amber-950/35 px-2.5 py-2 text-[10px] text-amber-950 dark:text-amber-100/95 leading-snug">
              <AlertTriangle className="shrink-0 w-3.5 h-3.5 text-amber-600 dark:text-amber-400 mt-0.5" aria-hidden />
              <p>
                <span className="font-black">Truncated.</span>{' '}
                Map shows up to {maxFeaturesPerLayer.toLocaleString()} features per layer. Extra:{' '}
                <span className="font-bold break-words">
                  {layersExceedingDisplayCap.map((l) => {
                    const c = l.data?.features?.length ?? 0;
                    return `${l.name} (${c.toLocaleString()})`;
                  }).join(', ')}
                </span>
                . Use{' '}
                <Cog className="inline w-3.5 h-3.5 align-[-0.15em] text-amber-800 dark:text-amber-200" aria-hidden />
                {' '}
                to raise the cap. Over {MAP_MAX_FEATURES_PER_LAYER_SLOW_WARN.toLocaleString()} may lag.
              </p>
            </div>
          )}
          <div className="min-h-0 max-h-[400px] flex-1 overflow-y-auto overscroll-contain pr-2 custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-300">
            <Reorder.Group
              axis="y"
              values={displayLayers}
              onReorder={handleReorder}
              className="space-y-1.5 py-1"
            >
              <AnimatePresence initial={false}>
                {displayLayers.map((layer, index) => (
                  <Reorder.Item
                    key={layer.id}
                    value={layer}
                    dragListener={!readOnly}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    {index > 0 && (
                      <div className="h-px bg-gray-200 dark:bg-gray-700 mb-1.5" />
                    )}
                    <MapLayerItem
                      layer={layer}
                      isActive={activePickerId === layer.id}
                      onToggleVisibility={onToggleVisibility}
                      onRemove={handleRemove}
                      onUpdateTheme={onUpdateTheme}
                      onTogglePicker={handleTogglePicker}
                      onFilterLayer={onFilterLayer}
                      onFlyToLayer={onFlyToLayer}
                      isDraggable={!readOnly}
                      readOnly={readOnly}
                      runTour={tourLayerId === layer.id}
                      onTourFinish={handleLayerTourFinish}
                    />
                  </Reorder.Item>
                ))}
              </AnimatePresence>
              {displayLayers.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">No active layers</p>
                </div>
              )}
            </Reorder.Group>
          </div>
        </div>
        {activeLayer && (
          <ColorPicker
            layer={activeLayer}
            readOnly={readOnly}
            onClose={() => closePicker(true)}
            onUpdateTheme={onUpdateTheme}
            onUpdateLayerClassification={onUpdateLayerClassification}
            onUpdateLayerMetric={onUpdateLayerMetric}
            onUpdateLayerColorProperty={onUpdateLayerColorProperty}
          />
        )}
        </>
      )}
    </div>
  );
};

export default MapLayerList;
